import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Divider, 
  Collapse,
  styled,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Link,
  useTheme
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { getManagerPanel } from '../../services/migration/personnelDataService';
import { hasBuyerB2BForBranch } from '../../services/ecommerceApi';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

// İkonlar
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MessageIcon from '@mui/icons-material/Message';
import PollIcon from '@mui/icons-material/Poll';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DescriptionIcon from '@mui/icons-material/Description';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { resolveWarehousePermissions } from './useManagerWarehousePermissions';
import { resolveProfitLossPermission } from './useManagerProfitLossPermission';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import WorkIcon from '@mui/icons-material/Work';

const SidebarContainer = styled(Box)<{ isCollapsed: boolean }>(({ theme, isCollapsed }) => ({
  width: isCollapsed ? 70 : 260,
  height: '100%',
  backgroundColor: '#102648',
  color: 'white',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  borderRadius: '0',
  boxShadow: '0 0 10px rgba(0,0,0,0.3)',
  flexShrink: 0,
  transition: theme.transitions.create(['width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
}));

const Logo = styled(Box)<{ isCollapsed: boolean }>(({ theme, isCollapsed }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  borderBottom: '1px solid rgba(255,255,255,0.1)',
}));

const MenuContainer = styled(List)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(2, 0),
}));

const MenuItem = styled(ListItem)<{ active?: boolean; isCollapsed: boolean }>(({ theme, active, isCollapsed }) => ({
  padding: theme.spacing(1.5, isCollapsed ? 2 : 3),
  marginBottom: theme.spacing(0.5),
  cursor: 'pointer',
  borderLeft: active ? '4px solid white' : '4px solid transparent',
  backgroundColor: active ? 'rgba(255,255,255,0.1)' : 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
}));

const ToggleButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: -12,
  top: 55,
  backgroundColor: '#0D47A1',
  color: 'white',
  '&:hover': {
    backgroundColor: '#0D47A1',
  },
  zIndex: 101,
  border: '2px solid white',
  boxShadow: '0 0 5px rgba(0,0,0,0.2)',
}));

// Grup başlığı styled component
const GroupHeader = styled(ListItem)<{ isCollapsed: boolean }>(({ theme, isCollapsed }) => ({
  padding: theme.spacing(1.5, isCollapsed ? 2 : 3),
  cursor: 'pointer',
  color: 'white',
  backgroundColor: 'rgba(255,255,255,0.05)',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  marginTop: theme.spacing(1),
}));

// Tab yapılandırma arayüzü
interface TabConfig {
  id: string;
  label: string;
  icon: React.ReactNode;
  component: string;
  enabled: boolean;
  group: 'standalone' | 'sales' | 'taskTracking' | 'hr' | 'accounting';
}

// Grup tanımları
const GROUP_CONFIG = {
  accounting: { label: 'Ön Muhasebe', icon: <AccountBalanceWalletIcon /> },
  sales: { label: 'Satış Sistemi', icon: <StorefrontIcon /> },
  taskTracking: { label: 'Görev Takip', icon: <WorkIcon /> },
  hr: { label: 'İnsan Kaynakları', icon: <SupervisedUserCircleIcon /> },
};

interface ManagerSidebarProps {
  activeBranchId?: string | null;
  branchesLoading?: boolean;
  onTabChange: (component: string) => void;
  onCollapse: (isCollapsed: boolean) => void;
}

const ManagerSidebar: React.FC<ManagerSidebarProps> = ({ activeBranchId, branchesLoading, onTabChange, onCollapse }) => {
  const { currentUser, userDetails } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [tabs, setTabs] = useState<TabConfig[]>([]);
  const [hasAccountingDashboard, setHasAccountingDashboard] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  // Son işlenen aktif şube; şube değişince Siparişler görünürlüğü yeniden hesaplanır.
  const processedBranchRef = useRef<string | null | undefined>(undefined);
  const theme = useTheme();

  // Grup açık/kapalı durumları
  const [accountingOpen, setAccountingOpen] = useState(true);
  const [salesOpen, setSalesOpen] = useState(true);
  const [taskTrackingOpen, setTaskTrackingOpen] = useState(true);
  const [hrOpen, setHrOpen] = useState(true);

  // İzinlere göre tab ayarlarını yükle
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        if (!currentUser || !userDetails || !userDetails.companyId) {
          setError('Kullanıcı veya şirket bilgisi bulunamadı.');
          setLoading(false);
          return;
        }

        // İlk yükleme VEYA aktif şube değiştiyse çalışır; onTabChange kimlik
        // değişimiyle tetiklenen gereksiz tekrarları burada eler.
        const currentBranch = activeBranchId ?? userDetails.branchesId ?? null;
        // Şube listesi hâlâ yükleniyorsa B2B yok diye karar verme — id gelince tekrar çalışır.
        if (branchesLoading && !currentBranch) return;
        if (!isFirstLoad && processedBranchRef.current === currentBranch) return;
        processedBranchRef.current = currentBranch;

        setLoading(true);
        
        // Tab konfigürasyonlarını oluştur (varsayılan olarak tümü devre dışı, grup bilgisi ile)
        const allTabs: TabConfig[] = [
          // Ön Muhasebe grubu
          {
            id: 'accountingDashboard',
            label: 'Şube Yönetimi',
            icon: <AccountBalanceWalletIcon />,
            component: 'accountingDashboard',
            enabled: false,
            group: 'accounting'
          },
          {
            id: 'invoices',
            label: 'Faturalar',
            icon: <ReceiptIcon />,
            component: 'invoices',
            enabled: false,
            group: 'accounting'
          },
          {
            id: 'eInvoices',
            label: 'E-Fatura',
            icon: <DescriptionIcon />,
            component: 'eInvoices',
            enabled: false,
            group: 'accounting'
          },
          {
            id: 'profitLoss',
            label: 'Kâr-Zarar',
            icon: <AssessmentIcon />,
            component: 'profitLoss',
            enabled: false,
            group: 'accounting'
          },
          {
            id: 'branchPrices',
            label: 'Şube Fiyatları',
            icon: <LocalOfferIcon />,
            component: 'branchPrices',
            enabled: false,
            group: 'accounting'
          },
          {
            id: 'current',
            label: 'Cari Hesaplar',
            icon: <PeopleIcon />,
            component: 'current',
            enabled: false,
            group: 'accounting'
          },
          {
            id: 'bank',
            label: 'Banka',
            icon: <AccountBalanceIcon />,
            component: 'bank',
            enabled: false,
            group: 'accounting'
          },
          {
            id: 'cash',
            label: 'Kasa',
            icon: <LocalAtmIcon />,
            component: 'cash',
            enabled: false,
            group: 'accounting'
          },
          {
            id: 'orders',
            label: 'Siparişler',
            icon: <ShoppingCartIcon />,
            component: 'orders',
            enabled: false,
            group: 'accounting'
          },
          {
            id: 'warehouse',
            label: 'Depo',
            icon: <WarehouseIcon />,
            component: 'warehouse',
            enabled: false,
            group: 'accounting'
          },
          // Satış Sistemi grubu
          {
            id: 'qrmenu',
            label: 'QR Menü',
            icon: <QrCode2Icon />,
            component: 'qrmenu',
            enabled: false,
            group: 'sales'
          },
          {
            id: 'salesReports',
            label: 'Satış Raporları',
            icon: <AssessmentIcon />,
            component: 'salesReports',
            enabled: false,
            group: 'sales'
          },
          {
            id: 'endOfDayList',
            label: 'Gün Sonları',
            icon: <ReceiptLongIcon />,
            component: 'endOfDayList',
            enabled: false,
            group: 'sales'
          },
          // Görev Takip grubu
          {
            id: 'dashboard',
            label: 'GTS Dashboard',
            icon: <DashboardIcon />,
            component: 'dashboard',
            enabled: false,
            group: 'taskTracking'
          },
          {
            id: 'tasks',
            label: 'Görevler',
            icon: <AssignmentIcon />,
            component: 'tasks',
            enabled: false,
            group: 'taskTracking'
          },
          {
            id: 'reports',
            label: 'Raporlar',
            icon: <AssessmentIcon />,
            component: 'reports',
            enabled: false,
            group: 'taskTracking'
          },
          {
            id: 'messages',
            label: 'Mesajlar',
            icon: <MessageIcon />,
            component: 'messages',
            enabled: false,
            group: 'taskTracking'
          },
          {
            id: 'surveys',
            label: 'Anketler',
            icon: <PollIcon />,
            component: 'surveys',
            enabled: false,
            group: 'taskTracking'
          },
          // İnsan Kaynakları grubu
          {
            id: 'personnel',
            label: 'Personel',
            icon: <PeopleIcon />,
            component: 'personnel',
            enabled: false,
            group: 'hr'
          },
          {
            id: 'shifts',
            label: 'Vardiya',
            icon: <AccessTimeIcon />,
            component: 'shifts',
            enabled: false,
            group: 'hr'
          }
        ];
        
        // Faz 3: yonetici izinleri SQL'den (managerPanel SettingsJson); HybridFallback ile RTDB.
        let permissions: any = null;
        try {
          const mp = await getManagerPanel(currentUser.uid);
          if (mp.found && mp.settings) permissions = mp.settings;
        } catch (e) {
          console.warn('[ManagerSidebar] SQL managerPanel okuma hatasi:', e);
        }

        // Bu şube bir B2B satıcıya giriş yaptıysa (bağlantı veya gelen sipariş)
        // Siparişler menüsü o şubede otomatik açılır. Aksi halde eski yönetici izni geçerli.
        const effectiveBranchId = currentBranch;
        let branchHasB2B = false;
        if (effectiveBranchId) {
          try {
            branchHasB2B = await hasBuyerB2BForBranch(effectiveBranchId);
          } catch (e) {
            console.warn('[ManagerSidebar] B2B şube bağları okunamadı:', e);
          }
        }

        if (permissions) {
          
          // İzinlere göre tabları güncelle
          const enabledTabs = allTabs.map(tab => {
            // Bu şube B2B'ye giriş yaptıysa Siparişler açık; yoksa eski yönetici izni (orders).
            if (tab.id === 'orders') {
              return { ...tab, enabled: branchHasB2B || permissions['orders'] === true };
            }
            // ✅ Gün Sonları Listesi: salesReports ana yetkisi + salesReports_endOfDay alt yetkisi kontrolü
            if (tab.id === 'endOfDayList') {
              const hasSalesReports = permissions['salesReports'] === true;
              const subPerms = permissions['salesReports_subPermissions'];
              const hasEndOfDay = subPerms && subPerms['salesReports_endOfDay'] === true;
              return { ...tab, enabled: hasSalesReports && hasEndOfDay };
            }
            // Ön Muhasebe grubu yetkileri
            if (tab.id === 'accountingDashboard') {
              return { ...tab, enabled: permissions['accountingDashboard'] === true };
            }
            if (tab.id === 'invoices') {
              const hasAccounting = permissions['accountingDashboard'] === true;
              const subPerms = permissions['accountingDashboard_subPermissions'];
              const hasInvoices = subPerms && subPerms['accountingDashboard_invoices'] === true;
              return { ...tab, enabled: hasAccounting && hasInvoices };
            }
            if (tab.id === 'eInvoices') {
              const hasAccounting = permissions['accountingDashboard'] === true;
              const subPerms = permissions['accountingDashboard_subPermissions'];
              const hasEInvoices = subPerms && subPerms['accountingDashboard_eInvoices'] === true;
              return { ...tab, enabled: hasAccounting && hasEInvoices };
            }
            if (tab.id === 'branchPrices') {
              const hasAccounting = permissions['accountingDashboard'] === true;
              const subPerms = permissions['accountingDashboard_subPermissions'];
              const hasBranchPrices = subPerms && subPerms['accountingDashboard_branchPrices'] === true;
              return { ...tab, enabled: hasAccounting && hasBranchPrices };
            }
            if (tab.id === 'warehouse') {
              return { ...tab, enabled: resolveWarehousePermissions(permissions).canView };
            }
            if (tab.id === 'profitLoss') {
              return { ...tab, enabled: resolveProfitLossPermission(permissions) };
            }
            return { ...tab, enabled: permissions[tab.id] === true };
          });
          
          // Sadece etkin tabları filtreleyerek ayarla
          const filteredTabs = enabledTabs.filter(tab => tab.enabled);
          
          // Ön muhasebe dashboard yetkisi var mı kontrol et
          const hasAccDashboard = filteredTabs.some(t => t.id === 'accountingDashboard');
          setHasAccountingDashboard(hasAccDashboard);
          
          if (filteredTabs.length > 0) {
            setTabs(filteredTabs);
            const hasOrders = filteredTabs.some(t => t.id === 'orders');
            if (branchHasB2B && hasOrders) {
              setActiveTab('orders');
              onTabChange('orders');
            } else if (hasAccDashboard) {
              // Ön muhasebe dashboard yetkisi varsa onu ilk açılış sayfası yap
              setActiveTab('accountingDashboard');
              onTabChange('accountingDashboard');
            } else {
              setActiveTab(filteredTabs[0].component);
              onTabChange(filteredTabs[0].component);
            }
          } else {
            // Hiç etkin tab yoksa dashboard'u varsayılan olarak etkinleştir
            const defaultTab = { ...allTabs.find(t => t.id === 'dashboard')!, enabled: true };
            setTabs([defaultTab]);
            setActiveTab(defaultTab.component);
            onTabChange(defaultTab.component);
            setError('Size atanmış herhangi bir modül izni bulunmuyor. Varsayılan olarak GTS Dashboard görüntüleniyor.');
          }
        } else {
          const dashboardTab = { ...allTabs.find(t => t.id === 'dashboard')!, enabled: true };
          if (branchHasB2B) {
            const ordersTab = { ...allTabs.find(t => t.id === 'orders')!, enabled: true };
            setTabs([ordersTab, dashboardTab]);
            setActiveTab(ordersTab.component);
            onTabChange(ordersTab.component);
          } else {
            setTabs([dashboardTab]);
            setActiveTab(dashboardTab.component);
            onTabChange(dashboardTab.component);
            setError('Size atanmış herhangi bir modül izni bulunmuyor. Varsayılan olarak GTS Dashboard görüntüleniyor.');
          }
        }
        
        setIsFirstLoad(false);
        setLoading(false);
      } catch (error) {
        console.error('Yönetici izinleri alınırken hata:', error);
        setError('Yönetici izinleri yüklenirken bir hata oluştu.');
        
        // Hata durumunda varsayılan olarak dashboard'u göster
        const defaultTab: TabConfig = {
          id: 'dashboard',
          label: 'GTS Dashboard',
          icon: <DashboardIcon />,
          component: 'dashboard',
          enabled: true,
          group: 'taskTracking'
        };
        
        setTabs([defaultTab]);
        setActiveTab(defaultTab.component);
        onTabChange(defaultTab.component);
        setIsFirstLoad(false);
        setLoading(false);
      }
    };
    
    fetchPermissions();
  }, [currentUser, userDetails, onTabChange, isFirstLoad, activeBranchId, branchesLoading]);

  // Tab tıklanma
  const handleTabClick = (tab: TabConfig) => {
    setActiveTab(tab.component);
    onTabChange(tab.component);
  };

  const toggleSidebar = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    onCollapse(newCollapsedState);
  };

  // Gruba göre filtrelenmiş tabları al
  const getTabsByGroup = (group: string) => tabs.filter(t => t.group === group);
  const accountingTabs = getTabsByGroup('accounting');
  const salesTabs = getTabsByGroup('sales');
  const taskTrackingTabs = getTabsByGroup('taskTracking');
  const hrTabs = getTabsByGroup('hr');

  return (
    <SidebarContainer isCollapsed={isCollapsed}>
      <ToggleButton onClick={toggleSidebar} size="small">
        {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
      </ToggleButton>
      
      <Logo isCollapsed={isCollapsed}>
        {isCollapsed ? (
          <Typography variant="h4" fontWeight="bold" color="primary">
            Y
          </Typography>
        ) : (
          <Typography variant="h6" fontWeight="bold" align="center">
            Yönetici Paneli
          </Typography>
        )}
      </Logo>
      
      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: 300,
          width: isCollapsed ? 70 : 260
        }}>
          <CircularProgress color="inherit" />
        </Box>
      ) : (
        <>
          {error && !isCollapsed && (
            <Alert severity="warning" sx={{ m: 2, fontSize: 12 }}>
              {error}
            </Alert>
          )}
          
          <MenuContainer>
            {/* Ön Muhasebe Grubu */}
            {accountingTabs.length > 0 && (
              <>
                <GroupHeader 
                  onClick={() => setAccountingOpen(!accountingOpen)} 
                  isCollapsed={isCollapsed}
                  sx={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : 40, color: 'rgba(255,255,255,0.7)' }}>
                      {GROUP_CONFIG.accounting.icon}
                    </ListItemIcon>
                    {!isCollapsed && (
                      <ListItemText 
                        primary={GROUP_CONFIG.accounting.label} 
                        primaryTypographyProps={{ fontSize: 14, fontWeight: 'bold', color: 'rgba(255,255,255,0.7)' }} 
                      />
                    )}
                  </Box>
                  {!isCollapsed && (accountingOpen ? <ExpandLess sx={{ color: 'rgba(255,255,255,0.5)' }} /> : <ExpandMore sx={{ color: 'rgba(255,255,255,0.5)' }} />)}
                </GroupHeader>
                <Collapse in={accountingOpen} timeout="auto" unmountOnExit>
                  {accountingTabs.map((tab) => (
                    <MenuItem 
                      key={tab.id}
                      active={activeTab === tab.component} 
                      onClick={() => handleTabClick(tab)}
                      isCollapsed={isCollapsed}
                      sx={{ pl: isCollapsed ? 2 : 5 }}
                    >
                      <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : 40, color: activeTab === tab.component ? '#90caf9' : 'white' }}>
                        {tab.icon}
                      </ListItemIcon>
                      {!isCollapsed && (
                        <ListItemText 
                          primary={tab.label} 
                          primaryTypographyProps={{ 
                            fontSize: 14,
                            fontWeight: activeTab === tab.component ? 'bold' : 'normal',
                            color: activeTab === tab.component ? '#90caf9' : 'white'
                          }} 
                        />
                      )}
                    </MenuItem>
                  ))}
                </Collapse>
              </>
            )}

            {/* Satış Sistemi Grubu */}
            {salesTabs.length > 0 && (
              <>
                <GroupHeader 
                  onClick={() => setSalesOpen(!salesOpen)} 
                  isCollapsed={isCollapsed}
                  sx={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : 40, color: 'rgba(255,255,255,0.7)' }}>
                      {GROUP_CONFIG.sales.icon}
                    </ListItemIcon>
                    {!isCollapsed && (
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', fontSize: 12 }}>
                        {GROUP_CONFIG.sales.label}
                      </Typography>
                    )}
                  </Box>
                  {!isCollapsed && (salesOpen ? <ExpandLess sx={{ color: 'rgba(255,255,255,0.5)' }} /> : <ExpandMore sx={{ color: 'rgba(255,255,255,0.5)' }} />)}
                </GroupHeader>
                <Collapse in={salesOpen} timeout="auto" unmountOnExit>
                  {salesTabs.map((tab) => (
                    <MenuItem 
                      key={tab.id}
                      active={activeTab === tab.component} 
                      onClick={() => handleTabClick(tab)}
                      isCollapsed={isCollapsed}
                    >
                      <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : 40, color: 'white' }}>
                        {tab.icon}
                      </ListItemIcon>
                      {!isCollapsed && (
                        <ListItemText 
                          primary={tab.label} 
                          primaryTypographyProps={{ 
                            fontSize: 14,
                            fontWeight: activeTab === tab.component ? 'bold' : 'normal'
                          }} 
                        />
                      )}
                    </MenuItem>
                  ))}
                </Collapse>
              </>
            )}

            {/* ✅ Görev Takip Grubu */}
            {taskTrackingTabs.length > 0 && (
              <>
                <GroupHeader 
                  onClick={() => setTaskTrackingOpen(!taskTrackingOpen)} 
                  isCollapsed={isCollapsed}
                  sx={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : 40, color: 'rgba(255,255,255,0.7)' }}>
                      {GROUP_CONFIG.taskTracking.icon}
                    </ListItemIcon>
                    {!isCollapsed && (
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', fontSize: 12 }}>
                        {GROUP_CONFIG.taskTracking.label}
                      </Typography>
                    )}
                  </Box>
                  {!isCollapsed && (taskTrackingOpen ? <ExpandLess sx={{ color: 'rgba(255,255,255,0.5)' }} /> : <ExpandMore sx={{ color: 'rgba(255,255,255,0.5)' }} />)}
                </GroupHeader>
                <Collapse in={taskTrackingOpen} timeout="auto" unmountOnExit>
                  {taskTrackingTabs.map((tab) => (
                    <MenuItem 
                      key={tab.id}
                      active={activeTab === tab.component} 
                      onClick={() => handleTabClick(tab)}
                      isCollapsed={isCollapsed}
                    >
                      <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : 40, color: 'white' }}>
                        {tab.icon}
                      </ListItemIcon>
                      {!isCollapsed && (
                        <ListItemText 
                          primary={tab.label} 
                          primaryTypographyProps={{ 
                            fontSize: 14,
                            fontWeight: activeTab === tab.component ? 'bold' : 'normal'
                          }} 
                        />
                      )}
                    </MenuItem>
                  ))}
                </Collapse>
              </>
            )}

            {/* ✅ İnsan Kaynakları Grubu */}
            {hrTabs.length > 0 && (
              <>
                <GroupHeader 
                  onClick={() => setHrOpen(!hrOpen)} 
                  isCollapsed={isCollapsed}
                  sx={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : 40, color: 'rgba(255,255,255,0.7)' }}>
                      {GROUP_CONFIG.hr.icon}
                    </ListItemIcon>
                    {!isCollapsed && (
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', fontSize: 12 }}>
                        {GROUP_CONFIG.hr.label}
                      </Typography>
                    )}
                  </Box>
                  {!isCollapsed && (hrOpen ? <ExpandLess sx={{ color: 'rgba(255,255,255,0.5)' }} /> : <ExpandMore sx={{ color: 'rgba(255,255,255,0.5)' }} />)}
                </GroupHeader>
                <Collapse in={hrOpen} timeout="auto" unmountOnExit>
                  {hrTabs.map((tab) => (
                    <MenuItem 
                      key={tab.id}
                      active={activeTab === tab.component} 
                      onClick={() => handleTabClick(tab)}
                      isCollapsed={isCollapsed}
                    >
                      <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : 40, color: 'white' }}>
                        {tab.icon}
                      </ListItemIcon>
                      {!isCollapsed && (
                        <ListItemText 
                          primary={tab.label} 
                          primaryTypographyProps={{ 
                            fontSize: 14,
                            fontWeight: activeTab === tab.component ? 'bold' : 'normal'
                          }} 
                        />
                      )}
                    </MenuItem>
                  ))}
                </Collapse>
              </>
            )}
          </MenuContainer>
          
          <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
          
          <Box sx={{ 
            mt: 'auto', 
            p: 2, 
            textAlign: 'center',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <Link 
              href="https://www.mt-teknoloji.com" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              {!isCollapsed ? (
                <>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'white',
                      opacity: 0.8,
                      mb: 0.5
                    }}
                  >
                    Powered by
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: 'white',
                      fontWeight: 'bold',
                      '&:hover': {
                        color: theme.palette.primary.light,
                        transition: 'color 0.2s'
                      }
                    }}
                  >
                    AQUASOFT
                  </Typography>
                </>
              ) : (
                <Typography 
                  variant="h5" 
                  sx={{ 
                    color: 'white',
                    fontWeight: 'bold',
                    '&:hover': {
                      color: theme.palette.primary.light,
                      transition: 'color 0.2s'
                    }
                  }}
                >
                  A
                </Typography>
              )}
            </Link>
          </Box>
        </>
      )}
    </SidebarContainer>
  );
};

export default ManagerSidebar; 